import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtDate, fmtCurrency } from "../../lib/status";

export function VehicleMaintenanceTab({ vehicleId, editAllowed }) {
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const blank = {
    maintenance_type: "repair", title: "", date: "", odometer_km: "",
    cost_php: "", vendor: "", notes: "", next_due_date: "", status: "planned",
    next_due_mileage: "", interval_km: "", interval_days: "",
  };
  const [form, setForm] = useState(blank);

  const reload = useCallback(async () => {
    const { data } = await api.get(`/vehicles/${vehicleId}/maintenance`);
    setItems(data.items || []);
  }, [vehicleId]);
  useEffect(() => { reload(); }, [reload]);

  const cancel = () => { setShowForm(false); setEditingId(null); setForm(blank); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Give the record a title."); return; }
    setSaving(true);
    try {
      const payload = {
        maintenance_type: form.maintenance_type,
        title: form.title.trim(),
        date: form.date || null,
        odometer_km: form.odometer_km === "" ? null : parseFloat(form.odometer_km),
        cost_php: form.cost_php === "" ? null : parseFloat(form.cost_php),
        vendor: form.vendor?.trim() || null,
        notes: form.notes?.trim() || null,
        next_due_date: form.next_due_date || null,
        next_due_mileage: form.next_due_mileage === "" ? null : parseFloat(form.next_due_mileage),
        interval_km: form.interval_km === "" ? null : parseFloat(form.interval_km),
        interval_days: form.interval_days === "" ? null : parseInt(form.interval_days, 10),
        status: form.status,
      };
      if (editingId) {
        await api.put(`/vehicles/${vehicleId}/maintenance/${editingId}`, payload);
        toast.success("Maintenance updated");
      } else {
        await api.post(`/vehicles/${vehicleId}/maintenance`, payload);
        toast.success("Maintenance added");
      }
      cancel(); await reload();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    try {
      await api.delete(`/vehicles/${vehicleId}/maintenance/${m.id}`);
      toast.success("Removed"); await reload();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const markCompleted = async (m) => {
    try {
      await api.put(`/vehicles/${vehicleId}/maintenance/${m.id}`, { status: "completed" });
      toast.success("Marked completed"); await reload();
    } catch (ex) { toast.error(formatApiError(ex)); }
  };

  const dueTone = (s) => s === "overdue"
    ? "bg-red-100 text-red-700 border-red-200"
    : s === "soon" ? "bg-amber-100 text-amber-800 border-amber-200"
    : "bg-slate-100 text-slate-700 border-slate-200";

  // Combines the date and mileage dimensions (either may be absent) into one
  // pill label, matching the "due in N km" / "overdue by N days" phrasing.
  const dueLabel = (m) => {
    const parts = [];
    if (m.next_due_days != null) {
      parts.push(m.next_due_days < 0
        ? `${Math.abs(m.next_due_days)}d overdue` : `due in ${m.next_due_days}d`);
    }
    if (m.next_due_km != null) {
      parts.push(m.next_due_km < 0
        ? `${Math.abs(Math.round(m.next_due_km)).toLocaleString()} km overdue`
        : `due in ${Math.round(m.next_due_km).toLocaleString()} km`);
    }
    if (parts.length) return parts.join(" · ");
    return m.next_due_state === "overdue" ? "Overdue" : m.next_due_state === "soon" ? "Due soon" : "OK";
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="vd-maintenance">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold">Maintenance &amp; Repairs</h2>
          <p className="text-xs text-slate-500 mt-0.5">Service history, scheduled repairs, next-due reminders: internal-only.</p>
        </div>
        {editAllowed && !showForm && (
          <button
            type="button"
            onClick={() => { setForm(blank); setEditingId(null); setShowForm(true); }}
            data-testid="vd-maint-add"
            className="px-3 py-2 rounded-lg bg-[#1B4332] text-white hover:bg-[#2D6A4F] text-sm inline-flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" /> Add maintenance
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="vd-maint-form" className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Type</span>
              <select value={form.maintenance_type} onChange={(e) => setForm({ ...form, maintenance_type: e.target.value })}
                data-testid="vd-maint-type"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]">
                <option value="oil_change">Oil change</option>
                <option value="tires">Tires</option>
                <option value="brakes">Brakes</option>
                <option value="battery">Battery</option>
                <option value="engine">Engine</option>
                <option value="cleaning">Cleaning / detailing</option>
                <option value="inspection_checkup">Inspection / checkup</option>
                <option value="repair">Repair</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Title *</span>
              <input type="text" required maxLength={200} value={form.title}
                data-testid="vd-maint-title"
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Date</span>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Odometer (km)</span>
              <input type="number" min="0" step="any" value={form.odometer_km}
                onChange={(e) => setForm({ ...form, odometer_km: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Cost (₱)</span>
              <input type="number" min="0" step="any" value={form.cost_php}
                onChange={(e) => setForm({ ...form, cost_php: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Vendor / mechanic</span>
              <input type="text" maxLength={200} value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Next due (date)</span>
              <input type="date" value={form.next_due_date}
                onChange={(e) => setForm({ ...form, next_due_date: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Status</span>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                data-testid="vd-maint-status"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]">
                <option value="planned">Planned</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </label>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Next due (mileage, km)</span>
              <input type="number" min="0" step="any" value={form.next_due_mileage}
                data-testid="vd-maint-next-due-mileage"
                onChange={(e) => setForm({ ...form, next_due_mileage: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Repeat every (km)</span>
              <input type="number" min="0" step="any" value={form.interval_km}
                placeholder="e.g. 10000"
                onChange={(e) => setForm({ ...form, interval_km: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Repeat every (days)</span>
              <input type="number" min="0" step="1" value={form.interval_days}
                placeholder="e.g. 180 for 6 months"
                onChange={(e) => setForm({ ...form, interval_days: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
            </label>
          </div>
          <p className="text-xs text-slate-500">
            Leave &ldquo;Repeat every&rdquo; blank for a one-off fixed due date/mileage. Set it and mark this record
            <em> Completed</em> to auto-project the next due point forward.
          </p>
          <label className="block text-sm">
            <span className="text-slate-700 font-medium">Notes</span>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} maxLength={4000}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]" />
          </label>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button type="submit" disabled={saving} data-testid="vd-maint-save"
              className="px-4 py-2 rounded-lg bg-[#1B4332] text-white hover:bg-[#2D6A4F] text-sm font-medium disabled:opacity-60">
              {saving ? "Saving…" : editingId ? "Save changes" : "Add record"}
            </button>
            <button type="button" onClick={cancel} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">Cancel</button>
          </div>
        </form>
      )}

      {items === null ? (
        <div className="text-sm text-slate-500 py-2">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-slate-500 py-2" data-testid="vd-maint-empty">
          No service records yet. Add your oil changes, tire rotations, repairs to keep history together.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((m) => (
            <li key={m.id} className="py-3 flex items-center justify-between gap-3" data-testid={`vd-maint-${m.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{m.title}</div>
                <div className="text-xs text-slate-500">
                  <span className="capitalize">{(m.maintenance_type || "other").replace(/_/g, " ")}</span>
                  {m.date && <span className="ml-2">· {fmtDate(m.date)}</span>}
                  {m.odometer_km != null && <span className="ml-2">· {m.odometer_km.toLocaleString()} km</span>}
                  {m.cost_php != null && <span className="ml-2">· {fmtCurrency(m.cost_php)}</span>}
                  {m.vendor && <span className="ml-2">· {m.vendor}</span>}
                </div>
                {m.notes && <div className="text-xs text-slate-600 mt-1 whitespace-pre-line line-clamp-2">{m.notes}</div>}
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className={`text-[11px] px-2 py-0.5 rounded-full border capitalize ${
                  m.status === "completed" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : m.status === "overdue" ? "bg-red-50 border-red-200 text-red-700"
                    : m.status === "cancelled" ? "bg-slate-100 border-slate-200 text-slate-600"
                    : "bg-amber-50 border-amber-200 text-amber-800"
                }`}>
                  {m.status.replace(/_/g, " ")}
                </span>
                {m.next_due_state && (
                  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${dueTone(m.next_due_state)}`}
                        data-testid={`vd-maint-${m.id}-due`}>
                    {dueLabel(m)}
                  </span>
                )}
                {editAllowed && (
                  <div className="flex items-center gap-1 mt-1">
                    {m.status !== "completed" && (
                      <button onClick={() => markCompleted(m)} data-testid={`vd-maint-${m.id}-done`}
                        className="text-[11px] text-emerald-700 hover:underline">Mark done</button>
                    )}
                    <button onClick={() => { setForm({ ...m, odometer_km: m.odometer_km ?? "", cost_php: m.cost_php ?? "", date: m.date || "", next_due_date: m.next_due_date || "", vendor: m.vendor || "", notes: m.notes || "", next_due_mileage: m.next_due_mileage ?? "", interval_km: m.interval_km ?? "", interval_days: m.interval_days ?? "" }); setEditingId(m.id); setShowForm(true); }}
                      className="text-[11px] text-slate-600 hover:underline">Edit</button>
                    <button onClick={() => remove(m)} data-testid={`vd-maint-${m.id}-delete`}
                      className="text-[11px] text-red-600 hover:underline">Delete</button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
